import React from 'react';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import {
  getRemappedDocType,
  PATTERNS,
  OCRDetectedData,
  SelectOption,
  GUEST_FORM_NAMES,
} from '@chekin/core';
import {
  ANALYTIC_EVENTS,
  Analytics,
  getSetupFormData,
  useFetchLocations,
  useIVStore,
} from '@chekin/shared';
import {useSchema} from 'context/guest';
import {useSummary} from 'context/summary';

type SendRemappingErrorEventToSentryArgs = {
  originalDocType?: string;
  countryCode?: string;
  nationality?: string;
};
const sendRemappingErrorEventToSentry = ({
  originalDocType,
  countryCode,
  nationality,
}: SendRemappingErrorEventToSentryArgs) => {
  Sentry.captureMessage('Document type remapping failed', {
    level: 'warning',
    tags: {
      category: 'ocr_remapping',
      action: 'remapping_failed',
    },
    extra: {
      originalDocType,
      countryCode,
      nationality,
    },
    contexts: {
      remapping: {
        input: {
          originalDocType,
          countryCode,
          nationality,
        },
        result: null,
        reason: 'No matching document type or country combination found',
      },
    },
  });
};

const getValidName = (name?: string | null) => {
  if (!name) {
    return undefined;
  }
  return PATTERNS.name.test(name) ? name : undefined;
};

const getDocumentOption = (docType: string | null = '', options: SelectOption[] = []) => {
  if (!docType) {
    return;
  }
  const foundOption = options.find(document => {
    return document?.value === docType;
  });

  return (
    foundOption || {
      label: '',
      value: docType,
    }
  );
};

function useBuildOCRDetectedData() {
  const {locationsAsOptions} = useFetchLocations();
  const {countryCode} = useSummary();
  const {formFields} = useSchema();
  const docTypesOptions = formFields[GUEST_FORM_NAMES.document_type]?.options;
  const genderOptions = formFields[GUEST_FORM_NAMES.gender]?.options;
  const ivSetupData = useIVStore(getSetupFormData);

  const buildFormData = React.useCallback(
    (detectedData?: OCRDetectedData | null) => {
      if (!detectedData) {
        return {};
      }

      const isDateOfBirthValid =
        detectedData?.valid_date_of_birth && detectedData?.date_of_birth?.includes('-');
      const birthDate = isDateOfBirthValid
        ? dayjs(detectedData.date_of_birth).toDate()
        : undefined;

      const isIssueDateOfDocValid =
        detectedData?.valid_issue_date && detectedData?.issue_date?.includes('-');
      const issueDateOfDoc = isIssueDateOfDocValid
        ? dayjs(detectedData.issue_date).toDate()
        : undefined;

      const isExpirationDateOfDocValid =
        detectedData?.valid_expiration_date &&
        detectedData?.expiration_date?.includes('-');
      const expirationDateOfDoc = isExpirationDateOfDocValid
        ? dayjs(detectedData.expiration_date).toDate()
        : undefined;

      const nationalityCode = detectedData?.nationality || ivSetupData?.nationality;
      const documentTypeCode = detectedData?.type_doc || ivSetupData?.documentType?.value;

      const nationality = locationsAsOptions.find(l => {
        return l?.value === nationalityCode;
      });

      const remappedDocType = getRemappedDocType({
        docType: documentTypeCode,
        nationality: nationality?.value,
        countryCode,
      });

      if (!remappedDocType) {
        sendRemappingErrorEventToSentry({
          originalDocType: documentTypeCode,
          nationality: nationality?.value,
          countryCode,
        });
      }
      Analytics.track(ANALYTIC_EVENTS.ivParsingOfDetectedOCRData, {
        type_doc: documentTypeCode ?? '',
        remapped_doc_type: remappedDocType,
        nationality: detectedData?.nationality ?? '',
        country_code: countryCode,
      });

      const docTypeOption = getDocumentOption(remappedDocType, docTypesOptions);

      const genderOption = genderOptions?.find(gender => {
        return gender?.value === detectedData?.sex;
      });

      return {
        [GUEST_FORM_NAMES.gender]: genderOption,
        [GUEST_FORM_NAMES.document_issue_date]: issueDateOfDoc,
        [GUEST_FORM_NAMES.document_expiration_date]: expirationDateOfDoc,
        [GUEST_FORM_NAMES.birth_date]: birthDate,
        [GUEST_FORM_NAMES.nationality]: nationality,
        [GUEST_FORM_NAMES.document_type]: docTypeOption,
        [GUEST_FORM_NAMES.document_support_number]: detectedData?.support_number,
        [GUEST_FORM_NAMES.document_number]: detectedData?.number,
        [GUEST_FORM_NAMES.name]: getValidName(detectedData?.names),
        [GUEST_FORM_NAMES.surname]: getValidName(detectedData?.first_surname),
        [GUEST_FORM_NAMES.second_surname]: getValidName(detectedData?.second_surname),
      };
    },
    [countryCode, docTypesOptions, genderOptions, locationsAsOptions, ivSetupData],
  );

  return {buildFormData};
}

export default useBuildOCRDetectedData;
